import Typography from "@mui/material/Typography";
import {
    Button,
    Card,
    CardContent,
    Divider,
    FilledTextFieldProps,
    FormControl, InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedTextFieldProps,
    Select,
    Stack,
    StandardTextFieldProps,
    TextField,
    useTheme
} from "@mui/material";
import {JSXElementConstructor, Key, ReactElement, ReactNode, ReactPortal, useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import {BoltOutlined, InfoOutlined} from "@mui/icons-material";
import {SelectChangeEvent} from "@mui/material/Select";
import dayjs, {Dayjs} from "dayjs";
import "dayjs/locale/pt-br";
import {LocalizationProvider, MobileDateTimePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {MobileDatePicker} from '@mui/x-date-pickers/MobileDatePicker';
import {useLocation, useNavigate} from "react-router-dom";
import {useSnackbar} from "notistack";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {
    FindAllWithTeleobjectAlarmConditionReactQuery,
    FindPowerStationAndEquipmentNamesReactQuery,
    FindUnavailabilityByIdReactQuery, SaveUnavailabilityReactQuery
} from "./reactQuery/UnavailabilityReactQuery";
import {
    FindAllSkidsByPowerStationIdAndAlarmConditionReactQuery,
    FindAllSkidsByPowerStationIdReactQuery
} from "../../Settings/NotificationGroup/reactQuery/SkidReactQuery";
import {
    FindAllEquipmentsBySkidNullOrNotReactQuery,
    FindAllEquipmentsWithAvailabilityAlarmBySkidNullOrNotReactQuery
} from "../../Settings/Equipment/EquipmentReactQuery";
import {LocalDateTimeFormatISO} from "../../../Utils/DateFormatPatternUtils";


type DinDex = "DIN" | "DEX" | "OUTROS";


export default function UnavailabilityFormPage() {
    const CREATED_BY_TYPE = "USER" as const;

    // Status
    const UNAVAILABILITY_STATUS_OPEN_ID = 1; // Em Aberto
    const UNAVAILABILITY_STATUS_CLOSED_ID = 2; // Encerrado

    // Tipo
    const UNAVAILABILITY_TYPE_ID: Record<DinDex, number> = {
        DIN: 1,
        DEX: 2,
        OUTROS: 3,
    };


    const theme = useTheme();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const location = useLocation();
    const id = location.state?.id as string;

    const mutationFindById = FindUnavailabilityByIdReactQuery();
    const powerStationList = FindAllWithTeleobjectAlarmConditionReactQuery();



    useEffect(() => {
        if (id) {
            mutationFindById.mutate(Number(id), {
                onSuccess: (data) => {
                    console.log("data fetched by id: ", data);
                }
            });
        }
    }, [id]);

    const fetched = mutationFindById?.data?.data;



    // estados (UI)
    const [powerStationId, setPowerStationId] = useState<string>("");
    const{data: skids} = FindAllSkidsByPowerStationIdAndAlarmConditionReactQuery(Number(powerStationId));

    const [skidId, setSkidId] = useState<string>("");
    const [equipmentId, setEquipmentId] = useState<string>("");
    const{data: equipments} = FindAllEquipmentsWithAvailabilityAlarmBySkidNullOrNotReactQuery(Number(powerStationId), Number(skidId));
    const [dinDex, setDinDex] = useState<DinDex>("DIN");
    const mutationSave = SaveUnavailabilityReactQuery();

    dayjs.locale("pt-br");


    const [startAt, setStartAt] = useState<Dayjs | null>(dayjs());
    const [endAt, setEndAt] = useState<Dayjs | null>(null);

    const [description, setDescription] = useState<string>("");
    const [energyMwh, setEnergyMwh] = useState<string>("");

    const [motiveSelect, setMotiveSelect] = useState<string>("");
    const [reasonFreeText, setReasonFreeText] = useState<string>("");

    const isMotiveSelect = dinDex === "DIN";



    useEffect(() => {
        if (!fetched) return;

        const typeId = fetched.unavailabilityType?.id;
        const nextDinDex: DinDex = typeId === 2 ? "DEX" : typeId === 3 ? "OUTROS" : "DIN";
        const nextReason = (fetched.reason ?? "").trim();

        // The Select's MenuItem values are String(ps.id), so store the id (as string)
        setPowerStationId(fetched.powerStation?.id ? String(fetched.powerStation?.id) : "");
        setSkidId(fetched?.skid?.id ? String(fetched.skid.id) : "");
        setEquipmentId(fetched.equipment?.id ? String(fetched.equipment?.id) : "");
        setDinDex(nextDinDex);

        setStartAt(fetched.startDateTime ? dayjs(fetched.startDateTime) : null);
        setEndAt(fetched.endDateTime ? dayjs(fetched.endDateTime) : null);

        setDescription(fetched.description ?? "");
        setEnergyMwh(fetched.energyLossKwh != null ? String(fetched.energyLossKwh) : "");

        // reason vai para o campo certo
        if (nextDinDex === "DIN") {
            setMotiveSelect(nextReason);     // select
            setReasonFreeText("");          // limpa o texto livre
        } else {
            // DEX e OUTROS usam texto livre
            setReasonFreeText(nextReason);  // texto
            setMotiveSelect("");            // limpa o select
        }
    }, [fetched]);

    const powerStations = useMemo<any>(() => {
        return (powerStationList?.data?.data ?? []);
    }, [powerStationList?.data?.data]);

    const skidsList = useMemo<any>(() => {
        // ajuste conforme teu wrapper: pode ser skidsResp ou skidsResp.data
        return (skids?.data ?? skids ?? []);
    }, [skids]);

    const equipmentsList = useMemo<any>(() => {
        return (equipments?.data ?? equipments ?? []);
    }, [equipments]);




    const motiveOptions = useMemo(() => {
        return dinDex === "DIN" ? ["Preventiva", "Corretiva", "Outros"] : [];
    }, [dinDex]);

    const descriptionMax = 700;

    function handleCancel() {
        navigate(SystemRoutes.DASHBOARD_OVERVIEW_UNAVAILABILITY)
        console.log("cancelar");
    }


    function buildCreateUnavailabilityPayload(params: {
        id?: string | number | null;
        powerStationId: string;
        equipmentId?: string;
        skidId?: string;
        dinDex: DinDex;
        startAt: Dayjs | null;
        endAt: Dayjs | null;
        description: string;
        energyLossKwh: string | number | null | undefined;
        motiveSelect: string;
        reasonFreeText: string;
    }) {
        const {
            id,
            powerStationId,
            equipmentId,
            skidId,
            dinDex,
            startAt,
            endAt,
            description,
            energyLossKwh,
            motiveSelect,
            reasonFreeText
        } = params;

        console.log("energyLossKwh: ", energyLossKwh);

        const hasEnd = !!endAt;
        const energyRaw = energyLossKwh ?? "";
        const energyStr = String(energyRaw).trim();


        return {
            ...(id ? { id: Number(id) } : {}),
            powerStation: {
                id: Number(powerStationId)
            },

            equipment: equipmentId ? {
                id: Number(equipmentId)
            } : null,

            skid: skidId ? {
                id: Number(skidId)
            } : null,

            startDateTime: startAt ? LocalDateTimeFormatISO(startAt.toISOString()) : null,
            endDateTime: endAt ? LocalDateTimeFormatISO(endAt.toISOString()) : null,

            description,

            unavailabilityType: {
                id: UNAVAILABILITY_TYPE_ID[dinDex]
            },

            unavailabilityStatus: {
                id: hasEnd ? UNAVAILABILITY_STATUS_CLOSED_ID : UNAVAILABILITY_STATUS_OPEN_ID
            },

            createdByType: CREATED_BY_TYPE,

            energyLossKwh: energyStr ? Number(energyStr) : null,

            reason: dinDex === "DIN"
                ? motiveSelect
                : reasonFreeText
        };
    }

    function handleSubmit() {
        if (!powerStationId) {
            enqueueSnackbar("Selecione pelo menos uma usina!", {variant: "warning"});
            return;
        }

        if (!startAt) {
            enqueueSnackbar("Informe a data de início!", {variant: "warning"});
            return;
        }

        if (!energyMwh || String(energyMwh).trim() === "") {
            enqueueSnackbar("Informe a perda energética (kWh)!", {variant: "warning"});
            return;
        }

        if (endAt && startAt && endAt.isBefore(startAt)) {
            enqueueSnackbar("A data de término não pode ser anterior à data de início!", {variant: "warning"});
            return;
        }

        const payload = buildCreateUnavailabilityPayload({
            id,
            powerStationId,
            equipmentId,
            skidId,
            dinDex,
            startAt,
            endAt,
            description,
            energyLossKwh: energyMwh, // seu state atual
            motiveSelect,
            reasonFreeText,
        });

        console.log("Payload CREATE Unavailability:", payload);

        mutationSave.mutate(payload, {
            onSuccess: () => {
                // opcional: voltar pra lista
                navigate(SystemRoutes.DASHBOARD_OVERVIEW_UNAVAILABILITY);
            },
        });
    }

    return (
        <Box>
            {/* Título */}
            <Typography
                variant="h5"
                sx={{
                    fontFamily: theme.typography.fontFamily,
                    mb: 2,
                    fontWeight: 500,
                }}
            >
                Novo registro de indisponibilidade
            </Typography>

            <Divider sx={{mb: 2}}/>

            {/* Card principal */}
            <Card sx={{borderRadius: 2}}>
                <CardContent sx={{p: 3}}>
                    {/* Header "Informações" */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                        <InfoOutlined fontSize="large"/>
                        <Typography sx={{fontWeight: 500, fontSize: 25}}>Informações</Typography>
                    </Stack>

                    {/* Grid dos campos */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(4, 1fr)"
                            },
                            columnGap: 2,
                            rowGap: 2,
                        }}
                    >
                        {/* Linha 1 */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Usina</InputLabel>
                            <Select
                                label="Usina"
                                value={powerStationId}
                                onChange={(e: SelectChangeEvent) => {
                                    const nextPsId = e.target.value;
                                    setPowerStationId(nextPsId);
                                    setSkidId(""); // importante
                                    setEquipmentId(""); // importante
                                }}
                            >
                                {/* mock */}
                                {powerStations.map((ps: { id: Key | null | undefined; name: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }) => (
                                    <MenuItem key={ps.id} value={String(ps.id)}>
                                        {ps.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" disabled={!powerStationId}>
                            <InputLabel id="skid-label">Skid</InputLabel>
                            <Select
                                labelId="skid-label"
                                label="Skid"
                                value={skidId}
                                onChange={(e: SelectChangeEvent) => {
                                    const nextSkidId = e.target.value;
                                    setSkidId(nextSkidId);
                                    setEquipmentId(""); // troca a lista de equipamentos
                                }}
                            >
                                {skidsList.map((sk: { skidId: Key | null | undefined; skidName: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }) => (
                                    <MenuItem key={sk.skidId} value={String(sk.skidId)}>
                                        {sk.skidName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" disabled={!powerStationId}>
                            <InputLabel id="equip-label">Equipamento</InputLabel>
                            <Select
                                labelId="equip-label"
                                label="Equipamento"
                                value={equipmentId}
                                onChange={(e: SelectChangeEvent) => setEquipmentId(e.target.value)}
                            >
                                {equipmentsList.map((eq: { id: Key | null | undefined; name: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }) => (
                                    <MenuItem key={eq.id} value={String(eq.id)}>
                                        {eq.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel>DIN/DEX</InputLabel>
                            <Select
                                label="DIN/DEX"
                                value={dinDex}
                                onChange={(e) => {
                                    const v = e.target.value as DinDex;
                                    setDinDex(v);
                                    // reseta motivo conforme regra
                                    setMotiveSelect("");
                                    setReasonFreeText("");
                                }}
                            >
                                <MenuItem value="DIN">DIN</MenuItem>
                                <MenuItem value="DEX">DEX</MenuItem>
                                <MenuItem value="OUTROS">Outros</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Linha 2 */}
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <MobileDateTimePicker
                                label="Início"
                                value={startAt}
                                onChange={(newValue: any) => setStartAt(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" />
                                )}
                                />

                            <MobileDateTimePicker
                                label="Término"
                                value={endAt}
                                onChange={(newValue: any) => setEndAt(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" />
                                )}
                                />

                        </LocalizationProvider>

                        {/* Motivo: select se DIN/DEX, text se Outros */}
                        {isMotiveSelect ? (
                            <FormControl fullWidth size="small">
                                <InputLabel>Motivo</InputLabel>
                                <Select
                                    label="Motivo"
                                    value={motiveSelect}
                                    onChange={(e: SelectChangeEvent) => setMotiveSelect(e.target.value)}
                                >
                                    {motiveOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <TextField
                                fullWidth
                                size="small"
                                label="Motivo"
                                placeholder="Digite o motivo"
                                value={reasonFreeText}
                                onChange={(e) => setReasonFreeText(e.target.value)}
                            />
                        )}
                        <TextField
                            fullWidth
                            size="small"
                            label="Perda energética (kWh)"
                            value={energyMwh}
                            onChange={(e) => setEnergyMwh(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <BoltOutlined fontSize="medium" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Linha 3 (Descrição) */}
                        <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Descrição"
                                placeholder="Insira aqui a descrição da indisponibilidade..."
                                value={description}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setDescription(v.length > descriptionMax ? v.slice(0, descriptionMax) : v);
                                }}
                                multiline
                                minRows={3}
                            />
                            <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.secondary" }}>
                                {description.length}/{descriptionMax}
                            </Typography>
                        </Box>


                    </Box>

                    {/* Footer com botões */}
                    <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
                        <Button
                            variant="text"
                            onClick={handleCancel}
                        >
                            CANCELAR
                        </Button>
                        <Button variant="contained" onClick={handleSubmit}>
                            {id ? "SALVAR ALTERAÇÕES" : "CRIAR REGISTRO"}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}